import { ChangeEvent, Fragment, forwardRef, useContext, useRef, useState } from 'react'

import { Box, Hint, SearchField, dimensions } from '@factorialco/deprecated-design-system'
import * as Sentry from '@sentry/browser'
import { LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import { capitalize } from 'lodash'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useTextSearch } from 'modules/core/hooks/useTextSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import users from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'

import { employeeJobTitle } from 'modules/contracts/lib/utils'
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { type Employees } from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import ActionBar from 'modules/org_chart/components/ActionBar'
import Frame from 'modules/org_chart/components/Frame'
import Header from 'modules/org_chart/components/Header'
import Node from 'modules/org_chart/components/Node'
import type { NodeStatus } from 'modules/org_chart/components/Node/Employee'
import { NODE_SIZE } from 'modules/org_chart/components/Node/constants'
import { OrgChartStateContext } from 'modules/org_chart/components/Provider'
import SearchResultsNavigator from 'modules/org_chart/components/SearchResultsNavigator'
import SidePanel from 'modules/org_chart/components/SidePanel'
import TagInfoSelect from 'modules/org_chart/components/TagInfoSelect'
import useTree from 'modules/org_chart/hooks/useTree'
import useUncollapsedNodes from 'modules/org_chart/hooks/useUncollapsedNodes'
import useZoom from 'modules/org_chart/hooks/useZoom'
import linkGenerator from 'modules/org_chart/lib/linkGenerator'
import { takeSnapshot } from 'modules/org_chart/lib/takeSnapshot'
import { findTreeNodeById } from 'modules/org_chart/lib/tree'
import type { Dimensions } from 'modules/org_chart/models/Dimensions'
import type {
  AddPositionNodeData,
  EmployeeNodeData,
  PositionNodeData,
} from 'modules/org_chart/models/NodeData'
import type { PointNode } from 'modules/org_chart/models/PointNode'

export interface Props {
  heads: Employee[]
  employees: Employee[]
  size: Dimensions
  ref: React.Ref<SVGSVGElement>
  showLegalGenders: boolean
  canExport: boolean
  canSnapshot: boolean
  canManagePositions: boolean
  canSeeJobPostings: boolean
  canCreateJobPostings: boolean
  contractVersions: ContractVersions
  employeesCollection: Employees
}

const OrgChartRef = forwardRef<SVGSVGElement, Props>(function OrgChart(
  {
    heads,
    employees,
    size,
    showLegalGenders,
    canExport,
    canSnapshot,
    canManagePositions,
    canSeeJobPostings,
    canCreateJobPostings,
    contractVersions,
    employeesCollection,
  },
  ref
) {
  const { update, hintedNode, searchResultId, tagInfoProp } = useContext(OrgChartStateContext)
  const [width, height] = size
  const frameHeight = useMemo(() => height - parseInt(dimensions.s80), [height])
  const svgRef = useRef<SVGSVGElement>(null)
  const uncollapsedNodes = useUncollapsedNodes(new Set(heads.map((x) => x.attributes.id)))
  const tree = useTree(
    heads,
    employees,
    uncollapsedNodes.values,
    width,
    canManagePositions,
    canSeeJobPostings,
    canCreateJobPostings,
    employeesCollection
  )
  const zoom = useZoom(svgRef?.current, [width, frameHeight], tree.initialCoordinates)
  const isFullyUncollapsed = employees.length <= uncollapsedNodes.values.size
  const [hideOrgChartPositionsHints, setHideOrgChartPositionsHints] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideOrgChartPositionsTip
  )
  const [search, setSearch] = useState('')

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value
    setSearch(val)
  }

  const { results } = useTextSearch(employees, search, {
    fields: [],
    storeFields: [],
    extraFields: ['jobTitle', 'firstName', 'fullName'],
    extraStoreFields: ['fullName', 'firstName'] as const,
    fuzzy: 0.1,
    extractField: (employee, field) => {
      if (field === 'jobTitle') {
        return employeeJobTitle(contractVersions, employee)
      }

      if (field === 'firstName') {
        const userId = employee.attributes.access_id
        const user = users.get(userId)
        if (!user) {
          throw Error(`Error getting users with id = ${userId}`)
        }
        return user.attributes.first_name?.trim() || ''
      }

      if (field === 'fullName') {
        const userId = employee.attributes.access_id
        const user = users.get(userId)
        if (!user) {
          throw Error(`Error getting users with id = ${userId}`)
        }
        return user.fullName()
      }

      return employee[field as keyof Employee]
    },
  })

  const filteredEmployees = useUnsafeMemo(() => {
    if (!search) {
      return employees
    }
    const matchedIds = results.map((employee) => employee.id)
    const matchedEmployees = employees.filter((x) => matchedIds.includes(x.attributes.id))

    const filteredResult = matchedIds
      .map((id) => matchedEmployees.find((x) => x.attributes.id === id))
      .filter((employee): employee is Employee => employee !== undefined)

    return filteredResult
  }, [employees, results, search])

  const handleFindMe = () => {
    const currentEmployee = employees.find((employee) => {
      const userId = employee.attributes.access_id
      const user = users.get(userId)
      if (!user) {
        throw Error(`Error getting users with id = ${userId}`)
      }
      return user.isCurrent()
    })
    if (!currentEmployee) {
      // TODO;
      // Add modal to infom the user they arent' in the Org Chart
      return
    } else {
      track(trackingEvents.orgChart.findMeButton.click)
      handleResultChange(undefined, currentEmployee)
    }
  }

  const handleUncollapseAll = () => {
    if (isFullyUncollapsed) {
      uncollapsedNodes.reset()
      zoom.reset()
    } else {
      uncollapsedNodes.expand(employees.map((x) => x.attributes.id))

      const scaleAmount = zoom.scaleRange.MAX_ZOOM - zoom.scaleRange.MIN_ZOOM
      zoom.scale(scaleAmount)
    }
  }

  const handleResultChange = (
    result: Employee | undefined,
    currentEmployee: Employee | undefined
  ) => {
    if (!!search || !!currentEmployee) {
      const id = result ? result.attributes.id : currentEmployee?.attributes.id
      update('searchResult', id)

      if (!id) return

      // eslint-disable-next-line no-type-assertion/no-type-assertion
      const treeResult = (currentEmployee ? currentEmployee : result) as Employee
      const nextUncollapsedNodes = uncollapsedNodes.expand(
        tree.traceTreePathFromEmployee(treeResult),
        true
      )
      const nextTree = tree.build(heads, employees, nextUncollapsedNodes)
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      const nextPointNode = findTreeNodeById(nextTree, id) as PointNode

      zoom.navigateToNode(nextPointNode)
    }
  }

  const handleHintChange = () => {
    if (!hintedNode) return

    const manager = employees.find((x) => x.attributes.id === hintedNode.data.managerId)

    update('activeSidePanelTab', 'reports')
    update('activeEmployee', manager)
    setHideOrgChartPositionsHints(true)
    track(trackingEvents.orgChart.nodes.addPosition.hint.action)
  }

  const handleHintClose = () => {
    update('hintedNode', undefined)
    setHideOrgChartPositionsHints(true)
    track(trackingEvents.orgChart.nodes.addPosition.hint.close)
  }

  const handleHintFinish = () => {
    update('hintedNode', undefined)
    setHideOrgChartPositionsHints(true)
    track(trackingEvents.orgChart.sidepanel.assignJobsHint.action)
  }

  useUnsafeEffect(() => {
    if (!ref) return // eslint-disable-next-line no-type-assertion/no-type-assertion
    ;(ref as React.MutableRefObject<SVGSVGElement>).current = svgRef.current as SVGSVGElement
  }, [svgRef, ref])

  const employeeNode = (pointNode: PointNode<EmployeeNodeData>) => {
    const employee = pointNode.data.employee

    if (!employee) return

    const [width, height] = NODE_SIZE
    const id = employee.attributes.id

    const links = pointNode.children?.map((y, i: number) => {
      const link = linkGenerator({
        source: [pointNode.x + width / 2, pointNode.y + height],
        target: [y.x + width / 2, y.y],
      })

      return <Node.Link key={`${id}-link-${i}`} d={link} />
    })

    const status = ((): NodeStatus | undefined => {
      const isFiltered = filteredEmployees.includes(employee)

      if (searchResultId === id && search !== '') return 'highlighted'
      else if (filteredEmployees.length && !isFiltered) return 'dimmed'
    })()

    const handleNodeClick = () => {
      update('activeEmployee', employee)
      track(trackingEvents.orgChart.nodes.employee.click)
    }

    const handleCollapseChange = () => {
      const nextUncollapsedNodes = uncollapsedNodes.toggle(id)
      const nextTree = tree.build(heads, employees, nextUncollapsedNodes)
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      const nextPointNode = findTreeNodeById(nextTree, id) as PointNode

      if (nextUncollapsedNodes.has(id)) {
        zoom.navigateToCoordinates(nextPointNode.x, nextPointNode.y + height)
        track(trackingEvents.orgChart.nodes.managers.click)
      } else {
        zoom.navigateToNode(nextPointNode)
      }
    }

    return (
      <Fragment key={`employee-${id}`}>
        {links}
        <Node.Employee
          data={pointNode.data}
          tagInfoProp={tagInfoProp}
          x={pointNode.x}
          y={pointNode.y}
          status={status}
          onClick={handleNodeClick}
          onCollapseChange={handleCollapseChange}
          contractVersions={contractVersions}
        />
      </Fragment>
    )
  }

  const addPositionNode = (pointNode: PointNode<AddPositionNodeData>) => {
    if (!hintedNode && !hideOrgChartPositionsHints) {
      update('hintedNode', pointNode)
      zoom.navigateToNode(pointNode)
    }

    const node = (
      <Node.AddPosition
        key={`add-position-${pointNode.x}-${pointNode.y}`}
        x={pointNode.x}
        y={pointNode.y}
        data={pointNode.data}
        refetch={tree.refetch}
      />
    )

    return hintedNode?.data.managerId === pointNode.data.managerId ? (
      <Node.AddPosition.Hint
        key={`add-position-hint-${pointNode.x}-${pointNode.y}`}
        x={pointNode.x}
        y={pointNode.y}
      >
        {node}
      </Node.AddPosition.Hint>
    ) : (
      node
    )
  }

  const positionNode = (pointNode: PointNode<PositionNodeData>) => (
    <Node.Position
      key={`position-${pointNode.data.positionId}`}
      data={pointNode.data}
      x={pointNode.x}
      y={pointNode.y}
      refetch={tree.refetch}
    />
  )

  const treeNodes = tree.tree.map((x) => {
    switch (x.data.type) {
      case 'employee':
        return employeeNode(x as PointNode<EmployeeNodeData>)
      case 'position':
        return positionNode(x as PointNode<PositionNodeData>)
      case 'add-position':
        return addPositionNode(x as PointNode<AddPositionNodeData>)
      default:
        return null
    }
  })

  useUnsafeEffect(() => {
    if (svgRef.current) {
      // On first load zoom out a bit from the initial coordinates
      if (zoom.isReady) {
        zoom.scale(0.8)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom.isReady])

  const exportOrgChart = () => {
    try {
      app.setProgress({
        message: i18n.t('employees.orgchart.export.download'),
      })

      const url = buildUrl('/export/orgchart')
      window.location.href = url
    } catch (exception) {
      // eslint-disable-next-line no-console
      console.error(exception)
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_requesting'),
      })
    } finally {
      app.setProgress(null)
    }
  }
  const downloadPdf = () => {
    const svgElement = document.getElementById('orgchart-div')
    try {
      app.setProgress({
        message: i18n.t('employees.orgchart.download.message'),
      })

      if (!!svgElement) {
        takeSnapshot(svgElement)
      }
    } catch (exception) {
      // eslint-disable-next-line no-console
      console.error(exception)
      Sentry.captureException(exception)

      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_requesting'),
      })
    } finally {
      app.setProgress(null)
    }
  }

  const options = []
  const exportOption = {
    onClick: exportOrgChart,
    title: i18n.t('employees.orgchart.export.title'),
    description: i18n.t('employees.orgchart.export.description'),
  }

  const downloadOption = {
    onClick: downloadPdf,
    title: i18n.t('employees.orgchart.download.title'),
    description: i18n.t('employees.orgchart.download.description'),
  }

  if (canExport) {
    options.push(exportOption)
  }

  if (canSnapshot) {
    options.push(downloadOption)
  }

  return (
    <Hint.Popover.Provider
      steps={2}
      isHidden={!!hideOrgChartPositionsHints || !canManagePositions}
      onChange={handleHintChange}
      onClose={handleHintClose}
      onFinish={handleHintFinish}
    >
      <Box>
        <Header onFindMe={handleFindMe} options={options}>
          <Box>
            <TagInfoSelect
              initialValue={tagInfoProp}
              onTagInfoChange={(x) => update('tagInfoProp', x)}
              showLegalGenders={showLegalGenders}
            />
          </Box>
          <Box width="full">
            <SearchField
              variant="big"
              onSearch={onSearch}
              search={search}
              placeholder={capitalize(i18n.t('common.search'))}
            />
          </Box>
          <Box flexNoShrink>
            <SearchResultsNavigator
              results={filteredEmployees}
              search={search}
              onResultChange={handleResultChange}
            />
          </Box>
        </Header>
        <Box style={{ position: 'relative' }}>
          <Frame ref={svgRef} zoomValue={zoom.value} width={width} height={frameHeight}>
            {treeNodes}
          </Frame>
          {!!height && (
            <ActionBar
              onClick={zoom.scale}
              isFullyUncollapsed={isFullyUncollapsed}
              onUncollapseAll={handleUncollapseAll}
            />
          )}
          <SidePanel
            onClose={() => update('activeEmployee', undefined)}
            contractVersions={contractVersions}
            employees={employeesCollection}
          />
        </Box>
      </Box>
    </Hint.Popover.Provider>
  )
})

export default OrgChartRef
